import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Eraser, PenLine } from 'lucide-react';
import { toast } from 'sonner';

const ROLES_FIRMANTE = ['CLIENTE', 'TÉCNICO MANKLAR', 'MANKLAR', 'FIRMA DEL DEUDOR', 'TÉCNICO AUTORIZADO'];

export function SignatureModal({ documento, onClose, onSign }) {
  const rolesPendientes = (documento?.firmas_requeridas || [])
    .filter((f) => !f.sello_automatico && !f.firmado)
    .map((f) => f.rol);
  const roles = rolesPendientes.length > 0 ? rolesPendientes : ROLES_FIRMANTE;
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [nombre, setNombre] = useState('');
  const [dni, setDni] = useState('');
  const [rol, setRol] = useState(roles[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a1a6e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: ((point.clientX - rect.left) / rect.width) * canvas.width,
      y: ((point.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasInk(true);
  };

  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const handleConfirm = async () => {
    if (!nombre.trim()) {
      toast.error('Introduce el nombre del firmante');
      return;
    }
    if (!hasInk) {
      toast.error('Dibuja la firma en el recuadro');
      return;
    }
    setSaving(true);
    try {
      await onSign({
        rol_firmante: rol,
        firmante_nombre: nombre.trim(),
        firmante_dni: dni.trim() || null,
        firma_base64: canvasRef.current.toDataURL('image/png'),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-[#0a0a0a] border border-white/10 p-6" data-testid="firma-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white flex items-center gap-2">
            <PenLine className="w-5 h-5 text-sky-400" />
            Firmar: {documento?.template_nombre}
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Firma como</label>
              <select
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm"
                data-testid="firma-rol-select"
              >
                {roles.map((r) => (
                  <option key={r} value={r} className="bg-zinc-900">{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">DNI (opcional)</label>
              <Input
                value={dni}
                onChange={(e) => setDni(e.target.value)}
                placeholder="DNI / NIE"
                className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                data-testid="firma-dni-input"
              />
            </div>
          </div>
          <Input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Nombre y apellidos del firmante *"
            className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
            data-testid="firma-nombre-input"
          />

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-zinc-500">Firma aquí (dedo, stylus o ratón)</label>
              <button onClick={clear} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white" data-testid="firma-limpiar-btn">
                <Eraser className="w-3 h-3" />Limpiar
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
              className="w-full h-40 rounded-xl bg-white cursor-crosshair touch-none"
              data-testid="firma-canvas"
            />
          </div>

          <p className="text-[10px] text-zinc-600">
            Firma en pantalla (v1). El sistema está preparado para conectar una plataforma de firma electrónica certificada.
          </p>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-white/20 text-zinc-300">Cancelar</Button>
            <Button onClick={handleConfirm} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="firma-confirmar-btn">
              {saving ? 'Firmando...' : 'Confirmar firma'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
